import React, { useRef, useCallback, useEffect, useContext, useMemo } from 'react';
import usePaginatedFamilyThemes, { PaginatedFamilyTheme } from '../helpers/usePaginatedFamilyThemes';
import { PaginatedThemeCard } from './PaginatedThemeCard';
import Layout from '@xo-union/tk-component-layout';
import HeaderNav from '../HeaderNav';
import FooterNav from '@xo-union/tk-component-footer-nav/remote';
import Loader from '../Loader';
import ThemeFiltersVariantContext, { ThemeFilters } from '../helpers/ThemeFiltersContext';
import { NoFilterResults } from '../components/Filters/NoResults';
import { ThemesFilterHeader } from '../Themes/ThemesFilterHeader';
import styles from './PaginatedThemePicker.scss';
import { EndOfListMessage } from './EndofListMessage';
import { FeaturedThemes } from '../components/FeaturedThemes';
import useRecommendedThemesExperiment from './useRecommendedThemesExperiment';
import TrackingContext from '../helpers/TrackingContext';
import { getFirstOrNull } from '../helpers/ThemeContext';

const product = process.env.REACT_APP_ANALYTICS_PRODUCT;
const analyticsProps = { product };

const themeMatchesFilters = (
  theme: { categoryData?: any } | null | undefined,
  themeFilters: ThemeFilters | undefined
) => {
  const colors = themeFilters?.colors ?? null;
  const styles = themeFilters?.styles ?? null;
  const seasons = themeFilters?.seasons ?? null;

  const themeColors: string[] = Array.isArray(theme?.categoryData?.colors) ? theme?.categoryData?.colors : [];
  const themeStyles: string[] = Array.isArray(theme?.categoryData?.styles) ? theme?.categoryData?.styles : [];
  const themeSeasons: string[] = Array.isArray(theme?.categoryData?.seasons) ? theme?.categoryData?.seasons : [];

  return (
    (!colors?.length || colors.some(option => themeColors.includes(option))) &&
    (!styles?.length || styles.some(option => themeStyles.includes(option))) &&
    (!seasons?.length || seasons.some(option => themeSeasons.includes(option)))
  );
};

const filterPaginatedThemes = (
  themes: Array<{ family?: string | null; themes?: Array<any> | null }>,
  themeFilters: ThemeFilters | undefined
) => {
  const hasFilters = Boolean(
    themeFilters?.colors?.length || themeFilters?.styles?.length || themeFilters?.seasons?.length
  );
  if (!hasFilters) return themes;

  return themes.reduce((filtered, familyTheme) => {
    const matchingThemes = familyTheme.themes?.filter((t: any) => themeMatchesFilters(t, themeFilters)) ?? [];
    if (matchingThemes.length) {
      filtered.push({
        ...familyTheme,
        themes: matchingThemes,
      });
    }
    return filtered;
  }, [] as typeof themes);
};

export const PaginatedThemePicker = () => {
  const observerTarget = useRef<HTMLDivElement>(null);
  const hasTrackedImpression = useRef(false);
  const { themeFilters } = useContext(ThemeFiltersVariantContext);
  const { track } = useContext(TrackingContext);

  const { themes, loading, error, hasMore, loadMore } = usePaginatedFamilyThemes(10, themeFilters as any);

  const inRecommendedThemesExperiment = useRecommendedThemesExperiment();
  useEffect(() => {
    if (!loading && themes.length && !hasTrackedImpression.current) {
      hasTrackedImpression.current = true;
      track('Create Theme Grid Impression', {
        themeIdOrder: themes.map(t => getFirstOrNull(t.themes)?.id),
        themeOrder: themes.map(t => getFirstOrNull(t.themes)?.name),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, themes]);

  const filteredThemes = useMemo(
    () => filterPaginatedThemes(themes as PaginatedFamilyTheme[], themeFilters as ThemeFilters),
    [themes, themeFilters]
  );

  const isFiltersSet = useMemo(
    () => Boolean(themeFilters?.styles?.length || themeFilters?.colors?.length || themeFilters?.seasons?.length),
    [themeFilters]
  );

  const showFeaturedThemes = inRecommendedThemesExperiment && !isFiltersSet;

  const featuredThemes = useMemo(() => themes.slice(0, 6), [themes]);

  const gridThemes = useMemo(() => (showFeaturedThemes ? filteredThemes.slice(6) : filteredThemes), [
    showFeaturedThemes,
    filteredThemes,
  ]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  if (error) {
    return (
      <div className={styles.error}>
        <h3>Error loading themes</h3>
        <p>{error.message}</p>
      </div>
    );
  }

  return (
    <Layout header={<HeaderNav product={product} />} footer={<FooterNav analyticsProps={analyticsProps} />}>
      <div className={styles.container}>
        <ThemesFilterHeader />
        {showFeaturedThemes && <FeaturedThemes themes={featuredThemes} />}
        {/* Theme Grid */}

        <div className={styles.grid} data-testid="paginated-theme-grid">
          {gridThemes.map((family: any, index: number) => (
            <PaginatedThemeCard
              key={family.family ?? index}
              family={family}
              index={index}
              themesCount={themes.length}
            />
          ))}
        </div>

        {!loading && !filteredThemes.length && isFiltersSet ? <NoFilterResults /> : null}

        {/* Loading Indicator */}
        {loading && <Loader />}

        {/* Intersection Observer Target */}
        <div ref={observerTarget} style={{ height: '20px' }} />

        {/* End of List Message */}
        {!hasMore && themes.length > 0 && <EndOfListMessage />}
      </div>
    </Layout>
  );
};
